import { zodResolver } from '@hookform/resolvers/zod';
import { Close, CloudUpload, Send } from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { amazonColors } from '../../../config/amazonTheme';
import { ORDER_AGAIN_OPTIONS, REVIEW_HIGHLIGHTS, ReviewHighlight } from '../../../types/review';
import { compressImage } from '../../../utils/imageCompression';
import { CreateReviewFormData, getCreateReviewSchema } from '../../../validation/reviewSchema';
import StarRating from './StarRating';

interface ReviewFormProps {
  orderNumber: string;
  customerName?: string;
  onSubmit: (data: CreateReviewFormData, photos?: File[]) => Promise<void>;
  isLoading?: boolean;
}

const MAX_PHOTOS = 5;

const ReviewForm: React.FC<ReviewFormProps> = ({
  orderNumber,
  customerName = '',
  onSubmit,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<CreateReviewFormData>({
    resolver: zodResolver(getCreateReviewSchema()),
    defaultValues: {
      rating: 0,
      reviewerName: customerName || 'Not Required',
      comment: '',
      email: '',
      highlights: [],
      wouldOrderAgain: undefined,
      agreeToPublish: false,
    },
  });

  const selectedHighlights = watch('highlights') || [];
  const currentRating = watch('rating');

  // Mapping for highlights translation keys
  const highlightKeyMap: Record<string, string> = {
    'Delivered as promised': 'deliveredAsPromised',
    'Same price as U.S. stores': 'samePriceAsUS',
    'Easy ordering process': 'easyOrdering',
    'Helpful support': 'helpfulSupport',
    'Secure & careful packaging': 'securePackaging'
  };

  // Mapping for order again options translation keys
  const orderAgainKeyMap: Record<string, string> = {
    'Yes': 'yes',
    'Maybe': 'maybe',
    'No': 'no'
  };

  const handleFormSubmit = async (data: CreateReviewFormData) => {
    setSubmitError(null);
    try {
      // Add orderAgain to the data if needed (backend will handle)
      await onSubmit(data, uploadedPhotos.length > 0 ? uploadedPhotos : undefined);
    } catch (error: unknown) {
      // Extract error message from Axios error response or fallback
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message = axiosError?.response?.data?.message
        || (error instanceof Error ? error.message : 'Failed to submit review');
      setSubmitError(message);
    }
  };

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];
    const remainingSlots = MAX_PHOTOS - uploadedPhotos.length;

    if (files.length > remainingSlots) {
      setSubmitError(t('reviews.maxPhotosError', { max: MAX_PHOTOS }) || `Maximum ${MAX_PHOTOS} photos allowed`);
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    for (const file of filesToProcess) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSubmitError(t('reviews.invalidImageType') || 'Please upload image files only');
        continue;
      }
      // Compress image to under 2MB
      try {
        const compressedFile = await compressImage(file, 2 * 1024 * 1024);
        newFiles.push(compressedFile);
      } catch {
        setSubmitError(t('reviews.imageCompressionError') || 'Failed to process image');
        continue;
      }
    }

    if (newFiles.length === 0) return;

    // Create preview URLs for all new files
    const previewPromises = newFiles.map((file) => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previewPromises).then((previews) => {
      setUploadedPhotos((prev) => [...prev, ...newFiles]);
      setPhotoPreviews((prev) => [...prev, ...previews]);
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    setUploadedPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleHighlight = (
    highlight: ReviewHighlight,
    currentValue: string[] | undefined,
    onChange: (value: string[]) => void
  ) => {
    const current = currentValue || [];
    if (current.includes(highlight)) {
      onChange(current.filter((h) => h !== highlight));
    } else {
      onChange([...current, highlight]);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      className="notranslate"
      translate="no"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Header Message */}
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.grey[50],
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          textAlign: 'center',
        }}
      >
        <Typography 
          variant="body1" 
          fontWeight={600} 
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {t('reviews.orderDelivered')}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {t('reviews.takeThirtySeconds')}
        </Typography>
      </Box>

      {/* 1. Rating Section */}
      <Box>
        <Typography 
          variant="subtitle1" 
          fontWeight={600} 
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          1. {t('reviews.overallExperience')} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </Typography>
        <Controller
          name="rating"
          control={control}
          render={({ field }) => (
            <StarRating
              value={field.value}
              onChange={field.onChange}
              size="large"
              showLabel
            />
          )}
        />
        {errors.rating && (
          <FormHelperText error>{errors.rating.message}</FormHelperText>
        )}
      </Box>

      {/* 2. Name Field (Optional) */}
      <Box>
        <Typography 
          variant="subtitle1" 
          fontWeight={600} 
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          2. {t('reviews.yourName')}
        </Typography>
        <TextField
          {...register('reviewerName')}
          placeholder={t('reviews.namePlaceholder')}
          fullWidth
          error={!!errors.reviewerName}
          helperText={errors.reviewerName?.message}
          disabled={isLoading}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        />
      </Box>

      {/* 3. Highlights Section */}
      <Box>
        <Typography 
          variant="subtitle1" 
          fontWeight={600} 
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          3. {t('reviews.whatDidYouLike')}
        </Typography>
        <Controller
          name="highlights"
          control={control}
          render={({ field }) => (
            <Stack direction="column" gap={0}>
              {REVIEW_HIGHLIGHTS.map((highlight) => {
                const isSelected = (field.value || []).includes(highlight);
                return (
                  <Box
                    key={highlight}
                    onClick={() => toggleHighlight(highlight, field.value, field.onChange)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 0.5,
                      borderRadius: 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: isLoading ? 'transparent' : alpha(amazonColors.orange, 0.04),
                      },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isLoading}
                      size="small"
                      sx={{
                        padding: 0,
                        '&.Mui-checked': {
                          color: amazonColors.orange,
                        },
                      }}
                    />
                    <Typography 
                      variant="body2"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      {t(`reviews.highlights.${highlightKeyMap[highlight]}`)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        />
      </Box>

      {/* 3. Order Again Section */}
      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          4. {t('reviews.wouldYouOrderAgain')}
        </Typography>
        <Controller
          name="wouldOrderAgain"
          control={control}
          render={({ field }) => (
            <Stack direction="column" gap={0}>
              {ORDER_AGAIN_OPTIONS.map((option) => (
                <Box
                  key={option}
                  onClick={() => !isLoading && field.onChange(option)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 0.5,
                    borderRadius: 1,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isLoading ? 'transparent' : alpha(amazonColors.orange, 0.04),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: `2px solid ${field.value === option ? amazonColors.orange : theme.palette.grey[400]}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {field.value === option && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: amazonColors.orange,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {t(`reviews.orderAgain.${orderAgainKeyMap[option]}`)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        />
      </Box>

      {/* 4. Comment Field */}
      <Box>
        <Typography 
          variant="subtitle1" 
          fontWeight={600} 
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          5. {t('reviews.shortComment')}
        </Typography>
        <TextField
          {...register('comment')}
          placeholder={t('reviews.commentPlaceholder')}
          multiline
          rows={3}
          fullWidth
          error={!!errors.comment}
          helperText={errors.comment?.message}
          disabled={isLoading}
          inputProps={{ maxLength: 500 }}
        />
      </Box>

      {/* 5. Photo Upload */}
      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          6. {t('reviews.addPhotos')} ({uploadedPhotos.length}/{MAX_PHOTOS})
        </Typography>

        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handlePhotoChange}
          disabled={isLoading || uploadedPhotos.length >= MAX_PHOTOS}
        />

        {/* Photo Previews Grid */}
        {photoPreviews.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: 1,
              mb: 2,
            }}
          >
            {photoPreviews.map((preview, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.grey[300]}`,
                }}
              >
                <Box
                  component="img"
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  onClick={() => handleRemovePhoto(index)}
                  disabled={isLoading}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    padding: 0.5,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                  size="small"
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Add Photo Button */}
        {uploadedPhotos.length < MAX_PHOTOS && (
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            fullWidth
            sx={{
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.secondary,
              py: 2,
              '&:hover': {
                borderColor: amazonColors.orange,
                bgcolor: alpha(amazonColors.orange, 0.04),
              },
            }}
          >
            {uploadedPhotos.length === 0
              ? t('reviews.addPhotos')
              : t('reviews.addMorePhotos', { remaining: MAX_PHOTOS - uploadedPhotos.length }) || `Add more photos (${MAX_PHOTOS - uploadedPhotos.length} remaining)`
            }
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('reviews.photosHint', { max: MAX_PHOTOS }) || `JPG, PNG (up to ${MAX_PHOTOS} photos)`}
        </Typography>
      </Box>

      {/* 6. Agree to Publish */}
      <Box>
        <Controller
          name="agreeToPublish"
          control={control}
          render={({ field }) => (
            <Box
              onClick={() => !isLoading && field.onChange(!field.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 0.5,
                borderRadius: 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: isLoading ? 'transparent' : alpha(amazonColors.orange, 0.04),
                },
              }}
            >
              <Checkbox
                checked={field.value}
                disabled={isLoading}
                size="small"
                sx={{
                  padding: 0,
                  '&.Mui-checked': {
                    color: amazonColors.orange,
                  },
                }}
              />
              <Box>
                <Typography 
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  {t('reviews.agreeToPublish')}
                </Typography>
                {errors.agreeToPublish && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {errors.agreeToPublish.message}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          )}
        />
      </Box>

      {/* Error Alert */}
      {submitError && (
        <Alert severity="error" onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={isLoading || currentRating === 0}
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
        sx={{
          bgcolor: amazonColors.orange,
          color: 'white',
          fontWeight: 600,
          py: 1.5,
          '&:hover': {
            bgcolor: amazonColors.orangeHover || '#e67a00',
          },
          '&.Mui-disabled': {
            bgcolor: theme.palette.action.disabledBackground,
          },
        }}
      >
        {isLoading ? t('reviews.submitting') : t('reviews.submitReview')}
      </Button>

      {/* Thank You Message */}
      <Box
        sx={{
          textAlign: 'center',
          py: 2,
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary" 
          fontWeight={500}
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
         {t('reviews.thankYouTrusting')}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.625rem', sm: '0.75rem' }
          }}
        >
          {t('reviews.feedbackMatters')}
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(ReviewForm);
